import React, { useEffect, useRef } from 'react';

import { Update } from 'history';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { PreQualificationStep, setPreQualificationStep, setSignInStep } from 'prequalificationV2/actions';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import useBackListener from 'hooks/useBackListener';
import useScript from 'hooks/useScript';
import useAutoScroll from 'hooks/useScrollUp';
import Main from 'prequalificationV2/pages/Main';
import PreQualification from 'prequalificationV2/pages/PreQualification';
import { PasswordProtectedRoute } from 'pages/PasswordProtection/PasswordProtectedRoute';
import { ApplicationEnvironments } from 'enums/common.enums';
import { ENVIRONMENT, IS_CONCEPTUAL_HQ_ENABLED } from 'constants/constants';
import { connectConceptualHqSnippet, triggerHotjarEvent } from 'routes/routes.utils';
import SignInProcess from 'prequalificationV2/pages/SignInProcess';
import EnterNewPassword from 'prequalificationV2/pages/SignInProcess/steps/EnterNewPassword';
import Dashboard from 'prequalificationV2/pages/Dashboard/Dashboard';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import { SignInStep } from 'prequalificationV2/pages/SignInProcess/signInStep.enums';
import RedirectToNewContractorPortal from 'pages/ContractorPortal/RedirectToNewContractorPortal';
import AcknowledgementAndConsent from 'prequalificationV2/pages/PreQualification/steps/AcknowledgementAndConsent';
import PrivateRoute from 'prequalificationV2/routes/PrivateRoute';
import VerificationComplete from 'prequalificationV2/pages/PreQualification/steps/VerificationComplete';
import CompleteMobileAuth from 'prequalificationV2/pages/PreQualification/steps/CompleteMobileAuth';
import NotFound from 'prequalificationV2/pages/NotFound';
import Error from 'prequalificationV2/pages/Error/Error';
import NotQualified from 'prequalificationV2/pages/NotQualified/NotQualified';
import MobileSession from 'prequalificationV2/pages/Dashboard/Identity/ValidationId/MobileSession';
import ResumeProcessLink from 'prequalificationV2/pages/ResumeProcessLink';
import { PasswordProtection } from 'pages/PasswordProtection/PasswordProtection';
import { cacheUrlMarketingParamsToCookie } from 'prequalificationV2/routes/routes.utils';

export default () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const { currentPreQualificationStep, currentSignInStep, currentDashboardStep } = useAppSelector(
    (state) => state.stages,
  );
  const { pathname, search: query } = useLocation();

  const previousPreQualificationStepRef = useRef(currentPreQualificationStep);
  const previousSignInStepRef = useRef(currentSignInStep);
  const skipNextPreQualificationPushRef = useRef(false);
  const skipNextSignInPushRef = useRef(false);
  const currentSignInStepRef = useRef(currentSignInStep);

  useEffect(() => {
    currentSignInStepRef.current = currentSignInStep;
  }, [currentSignInStep]);

  useEffect(() => {
    if (!history?.state?.usr?.hfsPortal) {
      navigator(`${pathname}${query}`, {
        state: {
          hfsPortal: true,
          prequalificationStep: currentPreQualificationStep,
          signInStep: currentSignInStep,
        },
      });
    }
  }, []);

  useEffect(() => {
    const previousStep = previousPreQualificationStepRef.current;

    previousPreQualificationStepRef.current = currentPreQualificationStep;

    if (skipNextPreQualificationPushRef.current) {
      skipNextPreQualificationPushRef.current = false;

      return;
    }

    if (pathname === AppRoutes.Prequalification && previousStep !== currentPreQualificationStep) {
      navigator(AppRoutes.Prequalification, {
        state: { hfsPortal: true, prequalificationStep: currentPreQualificationStep },
        replace: false,
      });
    }
  }, [currentPreQualificationStep]);

  useEffect(() => {
    const previousStep = previousSignInStepRef.current;

    previousSignInStepRef.current = currentSignInStep;

    if (skipNextSignInPushRef.current) {
      skipNextSignInPushRef.current = false;

      return;
    }

    if (pathname === AppRoutes.Login && previousStep !== currentSignInStep) {
      navigator(AppRoutes.Login, {
        state: { hfsPortal: true, signInStep: currentSignInStep },
        replace: false,
      });
    }
  }, [currentSignInStep]);

  useEffect(() => {
    triggerHotjarEvent(pathname, currentPreQualificationStep);
    cacheUrlMarketingParamsToCookie();
  }, [pathname, currentPreQualificationStep, currentSignInStep, currentDashboardStep]);

  useBackListener(({ location }: Update) => {
    setTimeout(() => {
      const step = location.state as { prequalificationStep?: PreQualificationStep; signInStep?: SignInStep } | null;

      if (location.pathname === AppRoutes.Prequalification && step?.prequalificationStep) {
        skipNextPreQualificationPushRef.current = true;
        dispatch(setPreQualificationStep(step.prequalificationStep));

        return;
      }

      if (location.pathname === AppRoutes.Login && step?.signInStep) {
        skipNextSignInPushRef.current = true;
        dispatch(setSignInStep(step.signInStep));

        return;
      }

      // No history state to restore (e.g. the user landed on /login directly rather than via
      // an in-app push) — there's nowhere useful to go back to within the sign-in flow, so send
      // them forward into the flow instead of leaving them stuck on a bare sign-in screen.
      if (
        location.pathname === AppRoutes.Login &&
        !step?.signInStep &&
        currentSignInStepRef.current === SignInStep.SignIn
      ) {
        navigator(AppRoutes.Prequalification);

        return;
      }

      if (location.pathname === AppRoutes.Dashboard) {
        navigator(AppRoutes.Dashboard);
      }
    }, 0);
  });
  useScript(`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_API_KEY}&v=weekly`);

  if (ENVIRONMENT === ApplicationEnvironments.Production) {
    useScript(
      `https://tag.pearldiver.io/ldc.js?pid=${process.env.REACT_APP_FOUND_CHOSEN_PEARLDIVER_PID}&aid=${process.env.REACT_APP_FOUND_CHOSEN_PEARLDIVER_AID}`,
    );
  }

  if (IS_CONCEPTUAL_HQ_ENABLED) {
    connectConceptualHqSnippet();
  }

  useAutoScroll();

  return (
    <Routes>
      <Route path={''} element={<PasswordProtectedRoute />}>
        <Route path={AppRoutes.ContractorPortal} element={<RedirectToNewContractorPortal />} />
        <Route element={<Main />}>
          <Route path={AppRoutes.Home} element={<Navigate replace to={`${AppRoutes.Prequalification}${query}`} />} />
          <Route path={AppRoutes.Prequalification} element={<PreQualification />} />
          <Route path={AppRoutes.AcknowledgementAndConsent} element={<AcknowledgementAndConsent />} />
          <Route path={AppRoutes.Dashboard} element={<PrivateRoute />}>
            <Route path={AppRoutes.Dashboard} element={<Dashboard />} />
          </Route>
          <Route path={AppRoutes.LoanPortal} element={<Navigate replace to={`${AppRoutes.Dashboard}${query}`} />} />
          <Route path={AppRoutes.ResumeProcess} element={<ResumeProcessLink />} />
          <Route
            path={AppRoutes.OfferProcessing}
            element={<Navigate replace to={`${AppRoutes.Dashboard}${query}`} />}
          />
          <Route path={AppRoutes.Login} element={<SignInProcess />} />
          <Route path={AppRoutes.ResetPassword + `/:token`} element={<EnterNewPassword />} />
          <Route path={AppRoutes.CompleteAuthentication} element={<VerificationComplete />} />
          <Route path={AppRoutes.CompleteMobileAuthentication} element={<CompleteMobileAuth />} />
          <Route path={AppRoutes.Other} element={<NotFound />} />
          <Route path={AppRoutes.Error} element={<Error />} />
          <Route path={AppRoutes.NotQualified} element={<NotQualified />} />
          <Route path={AppRoutes.ValidationSession} element={<MobileSession />} />
        </Route>
      </Route>
      <Route path={AppRoutes.PasswordProtection} element={<PasswordProtection />} />
    </Routes>
  );
};
